import { useState } from 'react';
import { Lock, Unlock, LockOpen, Store, Loader2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { isKioskEnabled, setKioskEnabled, grantKioskBypass } from '@/lib/kioskLock';
import { getDeviceId } from '@/lib/deviceHeartbeat';

async function verifyManagerPin(pin) {
  // branch_id scopes the PIN lookup to this tenant; fall back to the tablet's saved POS branch, then its registered device id.
  const read = (k) => { try { return JSON.parse(localStorage.getItem(k) || 'null')?.branch_id || null; } catch { return null; } };
  const branch_id = read('cashier_user') || localStorage.getItem('pos_terminal_branch_id')
    || localStorage.getItem('pos_active_branch_id') || read('user') || null;
  const { data } = await api.post('/cashier/kiosk/verify-manager-pin', { pin, branch_id, device_id: getDeviceId() });
  return data;
}

// Full-screen gate shown when a kiosk-locked device tries to leave the POS.
export default function KioskLockScreen() {
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);

  const run = async (action) => {
    if (pin.length < 4) { toast.error('Enter the manager PIN'); return; }
    setBusy(true);
    try {
      const res = await verifyManagerPin(pin);
      if (action === 'once') {
        grantKioskBypass();
        toast.success(`Unlocked by ${res.name} — until the app is relaunched`);
      } else {
        setKioskEnabled(false);
        toast.success(`Kiosk lock turned off by ${res.name}`);
      }
      setTimeout(() => window.location.reload(), 400);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'PIN rejected');
      setPin('');
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-stone-900 via-stone-800 to-stone-900 p-4" data-testid="kiosk-lock-screen">
      <Card className="w-full max-w-sm border-0 shadow-2xl">
        <CardContent className="pt-8 pb-6 text-center space-y-4">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-orange-100 flex items-center justify-center">
            <Lock className="w-8 h-8 text-orange-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold font-outfit">Kiosk Mode</h1>
            <p className="text-sm text-muted-foreground mt-1">
              This device is locked to the POS. Enter a <strong>manager PIN</strong> to leave.
            </p>
          </div>
          <Input
            type="password"
            inputMode="numeric"
            placeholder="Manager PIN"
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
            className="h-12 text-center text-xl tracking-[0.5em]"
            data-testid="kiosk-pin-input"
            autoFocus
          />
          <div className="space-y-2">
            <Button
              className="w-full h-11 bg-orange-500 hover:bg-orange-600 text-white"
              onClick={() => run('once')}
              disabled={busy || pin.length < 4}
              data-testid="kiosk-unlock-once-btn"
            >
              {busy ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Unlock size={16} className="mr-2" />}
              Unlock this session
            </Button>
            <Button
              variant="outline"
              className="w-full h-11"
              onClick={() => run('disable')}
              disabled={busy || pin.length < 4}
              data-testid="kiosk-disable-btn"
            >
              <LockOpen size={16} className="mr-2" /> Turn off kiosk lock
            </Button>
          </div>
          <button
            className="text-sm text-orange-600 hover:underline inline-flex items-center gap-1.5"
            onClick={() => { window.location.href = '/cashier'; }}
            data-testid="kiosk-back-pos-btn"
          >
            <Store size={14} /> Back to POS
          </button>
        </CardContent>
      </Card>
    </div>
  );
}

// Compact toggle used on the Cashier login screen footer.
export function KioskToggleControl() {
  const [enabled, setEnabled] = useState(isKioskEnabled());
  const [askPin, setAskPin] = useState(false);
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);

  const enable = () => {
    setKioskEnabled(true);
    setEnabled(true);
    toast.success('Kiosk lock enabled — leaving the POS now requires a manager PIN');
  };

  const disable = async () => {
    if (pin.length < 4) { toast.error('Enter the manager PIN'); return; }
    setBusy(true);
    try {
      const res = await verifyManagerPin(pin);
      setKioskEnabled(false);
      setEnabled(false);
      setAskPin(false);
      setPin('');
      toast.success(`Kiosk lock turned off by ${res.name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'PIN rejected');
      setPin('');
    }
    setBusy(false);
  };

  if (!enabled) {
    return (
      <button
        onClick={enable}
        className="w-full mt-3 text-[11px] text-muted-foreground hover:text-orange-600 inline-flex items-center justify-center gap-1"
        data-testid="kiosk-toggle-btn"
      >
        <Lock size={11} /> Enable Kiosk Lock (lock this device to POS)
      </button>
    );
  }

  return (
    <div className="mt-3 text-center">
      {!askPin ? (
        <button
          onClick={() => setAskPin(true)}
          className="w-full text-[11px] text-emerald-600 inline-flex items-center justify-center gap-1"
          data-testid="kiosk-toggle-btn"
        >
          <ShieldCheck size={11} /> Kiosk Lock is ON — tap to disable (manager PIN)
        </button>
      ) : (
        <div className="flex gap-2 items-center justify-center mt-1">
          <Input
            type="password"
            inputMode="numeric"
            placeholder="Manager PIN"
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
            className="h-8 w-32 text-center text-sm"
            data-testid="kiosk-footer-pin-input"
          />
          <Button size="sm" className="h-8" onClick={disable} disabled={busy} data-testid="kiosk-footer-disable-btn">
            {busy ? <Loader2 size={12} className="animate-spin" /> : 'Disable'}
          </Button>
          <Button size="sm" variant="ghost" className="h-8" onClick={() => { setAskPin(false); setPin(''); }}>
            Cancel
          </Button>
        </div>
      )}
    </div>
  );
}
